import { useRef } from 'react'

import { PlanningBalancesByPeriodsQuery, PlanningBalancesQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import useVirtualization, { VirtualizationTypes } from 'modules/core/hooks/useVirtualization'

import Row from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Row'
import { gridColumn } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import type { ColumnsType } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import useFetchDefaultShifts from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchDefaultShifts'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]
type TimePlanningBalancesByPeriod = NonNullable<
  PlanningBalancesByPeriodsQuery['timePlanning']['balancesByPeriodsConnection']['nodes']
>[number]

interface VirtualList {
  shiftsRows: ShiftsRow[]
  itemHeight: number
  columns: ColumnsType
  scrollRef: React.RefObject<HTMLElement>
  preloadedElementsOutsideViewport?: number
  balancesByEmployee: Record<string, TimePlanningBalance>
  periodBalancesByEmployee: Record<string, TimePlanningBalancesByPeriod[]>
  disableVirtualization?: boolean
}

const VirtualList = ({
  shiftsRows,
  itemHeight,
  columns,
  scrollRef,
  preloadedElementsOutsideViewport,
  balancesByEmployee,
  periodBalancesByEmployee,
  disableVirtualization = false,
}: VirtualList) => {
  const heightRefs = useRef<Map<string, number | null | undefined>>(new Map())
  const visibleDefaultShifts = useFetchDefaultShifts({})

  const getHeight = (index: number): number | null | undefined => {
    const key = shiftsRows[index]?.key || '0'

    return heightRefs?.current.get(key)
  }

  const { visibleStartIndex, endIndex, topPlaceholderHeight, bottomPlaceholderHeight } =
    useVirtualization({
      items: shiftsRows,
      defaultItemHeight: itemHeight,
      getHeight,
      containerRef: scrollRef,
      type: VirtualizationTypes.VARIABLE,
      preloadedElementsOutsideViewport,
    })

  // Set the new height or the previous one if there was one
  const setHeightRef = (node: HTMLDivElement, shiftsRow: ShiftsRow) =>
    heightRefs.current.set(
      shiftsRow.key,
      node?.clientHeight || heightRefs.current.get(shiftsRow.key)
    )

  // When virtualization is disabled (e.g., for printing), render all rows
  const visibleRows = useUnsafeMemo(
    () => (disableVirtualization ? shiftsRows : shiftsRows.slice(visibleStartIndex, endIndex)),
    [shiftsRows, visibleStartIndex, endIndex, disableVirtualization]
  )

  return (
    <>
      {!disableVirtualization && (
        <div
          style={{ ...gridColumn(0, columns.length), height: `${topPlaceholderHeight}px` }}
          aria-hidden="true"
        />
      )}
      {visibleRows.map((shiftsRow) => (
        <Row
          key={`Row-${shiftsRow.key}`}
          ref={(node: HTMLDivElement) => setHeightRef(node, shiftsRow)}
          shiftsRow={shiftsRow}
          columns={columns}
          balances={balancesByEmployee[shiftsRow.id || '']}
          periodBalances={periodBalancesByEmployee[shiftsRow.id || '']}
          visibleDefaultShifts={visibleDefaultShifts.defaultShifts}
        />
      ))}
      {!disableVirtualization && (
        <div
          style={{ ...gridColumn(0, columns.length), height: `${bottomPlaceholderHeight}px` }}
          aria-hidden="true"
        />
      )}
    </>
  )
}

export default observer(VirtualList)
